import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import Loader from "./Loader";
import { useSession } from "../SessionContext";
import * as XLSX from 'xlsx';
import {
  IconReportAnalytics,
  IconSearch,
  IconDownload,
  IconEye,
  IconX,
  IconInfoCircle,
  IconSortAscending,
  IconSortDescending,
  IconCalendar,
} from "@tabler/icons-react";

//const IP = "http://localhost:8000";
const IP = "https://reportes.aux-rolplay.com";

const Simulator = () => {
  const { id_user, key } = useSession();
  const [rowData, setRowData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalUrl, setModalUrl] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showInfo, setShowInfo] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [activeStartDate, setActiveStartDate] = useState("");
  const [activeEndDate, setActiveEndDate] = useState("");
  const rowsPerPage = 20;

  useEffect(() => {
    if (id_user && key) {
      // Determinar si usar endpoint dummy o real
      const isDummyMode = id_user === "00" && key === "00";

      // Construir el endpoint con parámetro condicional para oksd2r446s
      let endpoint;
      if (isDummyMode) {
        endpoint = `${IP}/dummy-simulador/`;
      } else {
        endpoint = `${IP}/exec-query-simulador/?user_id=${id_user}&key=${key}`;

        // Agregar mb_idTag2 solo para la key oksd2r446s
        if (key === "oksd2r446s") {
          // Obtener mb_idTag2 de los parámetros de la URL
          const urlParams = new URLSearchParams(window.location.search);
          const mbIdTag2 = urlParams.get("mb_idTag2");
          const idAdmin = urlParams.get("idAdmin");

          if (mbIdTag2) {
            endpoint += `&mb_idTag2=${mbIdTag2}`;
          }

          if (idAdmin) {
            endpoint += `&idAdmin=${idAdmin}`;
          }
        }
      }

      fetch(endpoint)
        .then((response) => response.json())
        .then((data) => {
          setIsExiting(true);
          setTimeout(() => {
            setIsLoading(false);
            setRowData(data.result);
            toast.success(isDummyMode ? "Datos dummy cargados correctamente" : "Datos cargados correctamente");
          }, 500);
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
          setIsExiting(true);
          setTimeout(() => {
            setIsLoading(false);
            toast.error("Error al cargar los datos");
          }, 500);
        });
    }
  }, [id_user, key]);

  const openModal = (url) => {
    setModalUrl(url);
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setModalUrl("");
  };

  const downloadPDF = async () => {
    try {
      toast.info("Generando PDF", {
        description: "Por favor espera mientras se procesa el reporte...",
      });

      // Llamar al endpoint que convierte la URL a PDF
      const response = await fetch(`${IP}/download-pdf/?url=${encodeURIComponent(modalUrl)}`);

      if (response.ok) {
        // Crear un blob con el contenido del PDF
        const blob = await response.blob();

        // Crear URL temporal para el blob
        const downloadUrl = window.URL.createObjectURL(blob);

        // Crear elemento de descarga
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `reporte_${new Date().toISOString().slice(0, 10)}.pdf`;

        // Ejecutar la descarga
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Limpiar la URL temporal
        window.URL.revokeObjectURL(downloadUrl);

        toast.success("PDF descargado", {
          description: "El archivo se ha descargado correctamente",
        });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Error del servidor: ${response.status}`);
      }
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Error al descargar PDF", {
        description: error.message || "No se pudo generar el archivo PDF",
      });
    }
  };

  const onExportClick = () => {
    try {
      const excelContent = convertToExcel(sortedData);
      downloadExcel(excelContent, "simulator_data.xlsx");
      toast.success("Archivo descargado correctamente", {
        description: `Se exportaron ${sortedData.length} registros`,
      });
    } catch (error) {
      toast.error("Error al descargar el archivo", {
        description: "Por favor, intenta nuevamente",
      });
    }
  };

  // Definir las columnas usando useMemo
  const columns = useMemo(() => {
    const baseColumns = [
      { key: "sim_gerente", label: "Gerente", align: "left" },
      { key: "sim_user", label: "Nombre Usuario", align: "left" },
      { key: "sim_user_access", label: "Usuario", align: "left" },
      { key: "sim_user_agencia", label: "Sucursal", align: "left" },
      { key: "sim_user_ruta", label: "Ruta", align: "left" },
      { key: "sim_title", label: "Simulador", align: "left" },
      { key: "sim_score", label: "Calificación", align: "left" },
      { key: "sim_datetime", label: "Fecha", align: "left" },
      { key: "actions", label: "Acciones", align: "center" },
    ];
    return baseColumns;
  }, []);

  // Actualizar la función convertToExcel para generar formato Excel
  const convertToExcel = (data) => {
    const header = [
      ...columns.filter((col) => col.key !== "actions").map((col) => col.label),
      "URL Vista Previa",
    ];

    const rows = data.map((row) => [
      ...columns
        .filter((col) => col.key !== "actions")
        .map((col) => {
          const value = row[col.key];
          // Convertir explícitamente valores nulos, undefined o vacíos a "0"
          if (value === null || value === undefined || value === "") {
            // Solo convertir a "0" si la columna es numérica
            if (col.key.includes("score") || col.key === "sim_score") {
              return "0";
            }
            return "";
          }
          return value;
        }),
      row.sim_view_url || "",
    ]);

    // Crear workbook y worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);

    // Añadir la hoja al workbook
    XLSX.utils.book_append_sheet(wb, ws, "Simuladores");

    // Generar el archivo Excel
    return XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  };

  const downloadExcel = (content, fileName) => {
    const blob = new Blob([content], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const onFilterTextChange = (e) => {
    setSearchText(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Función de ordenamiento - añadir reset de página
  const sortData = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Función para formatear fecha de la DB
  const formatDBDate = (dateString) => {
    if (!dateString) return null;
    // Asumiendo que el formato es "YYYY-MM-DD HH:mm:ss"
    return dateString.split(" ")[0]; // Extraemos solo la parte de la fecha
  };

  // Modificar la función de filtrado por fecha
  const filterByDateRange = (data) => {
    if (!activeStartDate && !activeEndDate) return data;

    return data.filter((row) => {
      const rowDate = formatDBDate(row.sim_datetime);
      if (!rowDate) return false;

      if (activeStartDate && activeEndDate) {
        return rowDate >= activeStartDate && rowDate <= activeEndDate;
      } else if (activeStartDate) {
        return rowDate >= activeStartDate;
      } else if (activeEndDate) {
        return rowDate <= activeEndDate;
      }
      return true;
    });
  };

  // Añadir estas funciones para la navegación
  const goToStart = () => setCurrentPage(1);
  const goToEnd = () => setCurrentPage(totalPages);

  // Modificar el cálculo de datos filtrados para incluir búsqueda de texto
  const filteredData = useMemo(() => {
    let filtered = filterByDateRange(rowData);

    // Aplicar filtro de búsqueda de texto
    if (searchText) {
      filtered = filtered.filter((item) =>
        Object.values(item).some(
          (val) =>
            typeof val === "string" &&
            val.toLowerCase().includes(searchText.toLowerCase())
        )
      );
    }

    return filtered;
  }, [rowData, activeStartDate, activeEndDate, searchText]); // Añadir searchText a las dependencias

  // Aplicar ordenamiento a los datos filtrados
  const sortedData = useMemo(() => {
    const data = [...filteredData];
    if (sortConfig.key) {
      data.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return data;
  }, [filteredData, sortConfig]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return sortedData.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedData, currentPage]);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  // Modificar la condición del Loader
  if (isLoading) return <Loader isExiting={isExiting} />;

  return (
    <div className="w-full p-5">
      <div className="mb-6">
        <div className="p-4 border border-yellow-400 bg-yellow-50 rounded-xl text-yellow-800 text-sm flex items-center gap-2">
          <i className="fas fa-info-circle text-yellow-600"></i>
          <p>
            Para actualizar la información mostrada en la tabla, por favor
            recargue la página.
          </p>
        </div>
      </div>

      <div className="flex justify-between items-center mb-6">
        <button className="px-6 py-2.5 rounded-full text-sm font-medium bg-gradient-to-r from-[#dc2626] to-[#991b1b] text-white hover:from-[#991b1b] hover:to-[#dc2626] transition-all duration-300 shadow-md hover:shadow-xl flex items-center gap-2">
          <IconReportAnalytics size={18} />
          Simuladores
        </button>
        <div className="flex items-center gap-4">
          <button onClick={() => setShowInfo(true)} className="relative group">
            <div className="absolute -inset-3 bg-red-500/50 rounded-full blur opacity-75 group-hover:opacity-100 animate-ping"></div>
            <div className="relative w-8 h-8 bg-red-600 rounded-full text-white hover:bg-red-700 transition-colors duration-200 flex items-center justify-center">
              <IconInfoCircle size={18} strokeWidth={2.5} />
            </div>
          </button>
          <div className="relative">
            <IconSearch
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Buscar simulación..."
              value={searchText}
              onChange={onFilterTextChange}
              className="pl-10 pr-4 py-2.5 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#1b2a49] focus:border-transparent text-sm w-64 shadow-sm"
            />
          </div>
        </div>
      </div>

      {showInfo && (
        <div className="fixed inset-0 bg-black/70 flex justify-center items-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-11/12 max-w-2xl relative p-6 shadow-2xl">
            <button
              onClick={() => setShowInfo(false)}
              className="absolute -top-3 -right-3 w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700 transition-colors duration-200"
            >
              <IconX size={20} />
            </button>

            <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <IconInfoCircle size={24} className="text-red-600" />
              Guía de Simuladores
            </h2>

            <div className="space-y-4 text-gray-600">
              <div className="flex items-start gap-3">
                <IconSearch size={20} className="mt-1 text-gray-400 shrink-0" />
                <div>
                  <p className="font-medium text-gray-800">Búsqueda Avanzada</p>
                  <p className="text-sm">
                    Encuentra simulaciones específicas usando cualquier dato
                    como nombre, fecha, calificación o usuario
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <IconEye size={20} className="mt-1 text-gray-400 shrink-0" />
                <div>
                  <p className="font-medium text-gray-800">
                    Visualización de Resultados
                  </p>
                  <p className="text-sm">
                    Accede a los detalles completos de cada simulación usando el
                    botón "Ver"
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <IconCalendar
                  size={20}
                  className="mt-1 text-gray-400 shrink-0"
                />
                <div>
                  <p className="font-medium text-gray-800">
                    Filtrado por Fechas
                  </p>
                  <p className="text-sm">
                    Filtra los resultados por rango de fechas para un análisis
                    más específico
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <i className="fas fa-sort mt-1 text-gray-400"></i>
                <div>
                  <p className="font-medium text-gray-800">
                    Ordenamiento de Datos
                  </p>
                  <p className="text-sm">
                    Ordena la información haciendo clic en los encabezados de
                    las columnas
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <IconDownload
                  size={20}
                  className="mt-1 text-gray-400 shrink-0"
                />
                <div>
                  <p className="font-medium text-gray-800">
                    Exportación de Datos
                  </p>
                  <p className="text-sm">
                    Descarga los resultados filtrados en formato CSV para
                    análisis externos
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="w-full rounded-xl overflow-hidden bg-white shadow-lg">
        <div className="overflow-x-auto max-h-[600px] scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-gray-200 hover:scrollbar-thumb-gray-800 scrollbar-thumb-rounded-md scrollbar-track-rounded-md">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-[#991b1b] to-[#dc2626] text-white sticky top-0 z-10">
              <tr>
                {columns.map(({ key, label, align }) => (
                  <th
                    key={key}
                    onClick={() => key !== "actions" && sortData(key)}
                    className={`px-4 py-3 text-[13px] font-medium text-${align} whitespace-nowrap transition-colors duration-200 hover:bg-white/10 cursor-pointer group`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      {label}
                      {key !== "actions" && (
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                          {sortConfig.key === key ? (
                            sortConfig.direction === "asc" ? (
                              <IconSortAscending size={16} />
                            ) : (
                              <IconSortDescending size={16} />
                            )
                          ) : (
                            <IconSortAscending
                              size={16}
                              className="opacity-50"
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginatedData.map((row, index) => (
                <tr
                  key={index}
                  className="transition-all duration-200 hover:bg-gradient-to-r hover:from-red-50 hover:to-red-100 hover:shadow-sm even:bg-gray-50/50"
                >
                  {columns.map(({ key, align }) =>
                    key === "actions" ? (
                      <td
                        key={key}
                        className="px-4 py-2.5 text-[13px] text-center"
                      >
                        <button
                          onClick={() => openModal(row.sim_view_url)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-[#4B5563] text-white text-[13px] rounded-full hover:bg-red-600 transition-colors duration-300"
                        >
                          <IconEye size={16} />
                          Ver
                        </button>
                      </td>
                    ) : (
                      <td
                        key={key}
                        className={`px-4 py-2.5 text-[13px] ${key === "sim_title"
                          ? "text-center min-w-[200px]"
                          : "text-gray-600"
                          }`}
                      >
                        {key === "sim_title" ? (
                          <span className="inline-flex px-2.5 py-1 rounded-full bg-gradient-to-r from-red-100 to-red-200 text-red-800 border border-red-200 font-medium whitespace-nowrap">
                            {row[key]}
                          </span>
                        ) : (
                          row[key]
                        )}
                      </td>
                    ),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
          <div className="flex-1 flex justify-between items-center">
            <p className="text-sm text-gray-700">
              Mostrando{" "}
              <span className="font-medium">
                {sortedData.length === 0
                  ? 0
                  : (currentPage - 1) * rowsPerPage + 1}
              </span>{" "}
              -{" "}
              <span className="font-medium">
                {Math.min(currentPage * rowsPerPage, sortedData.length)}
              </span>{" "}
              de <span className="font-medium">{sortedData.length}</span>{" "}
              resultados
            </p>
            <div className="flex gap-2">
              <button
                onClick={goToStart}
                disabled={currentPage === 1}
                className={`px-4 py-2 text-sm rounded-full transition-all duration-200
                  ${currentPage === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-red-600 text-white hover:bg-red-700 hover:shadow-md"
                  }`}
              >
                Inicio
              </button>
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className={`px-4 py-2 text-sm rounded-full transition-all duration-200
                  ${currentPage === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-red-600 text-white hover:bg-red-700 hover:shadow-md"
                  }`}
              >
                Anterior
              </button>
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={currentPage === totalPages}
                className={`px-4 py-2 text-sm rounded-full transition-all duration-200
                  ${currentPage === totalPages
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-red-600 text-white hover:bg-red-700 hover:shadow-md"
                  }`}
              >
                Siguiente
              </button>
              <button
                onClick={goToEnd}
                disabled={currentPage === totalPages}
                className={`px-4 py-2 text-sm rounded-full transition-all duration-200
                  ${currentPage === totalPages
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-red-600 text-white hover:bg-red-700 hover:shadow-md"
                  }`}
              >
                Final
              </button>
            </div>
          </div>
        </div>
      </div>

      {modalVisible && (
        <div className="fixed inset-0 bg-black/70 flex justify-center items-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-11/12 max-w-6xl h-[85vh] relative flex flex-col shadow-2xl">
            <button
              onClick={closeModal}
              className="absolute -top-3 -right-3 w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700 transition-colors duration-200"
            >
              <IconX size={20} />
            </button>
            <div className="p-6 bg-gradient-to-r from-[#991b1b] to-[#dc2626] rounded-t-2xl flex items-center justify-between">
              <h2 className="text-white text-lg font-medium flex items-center gap-2">
                <i className="fas fa-chart-bar text-white/80"></i>
                Resultados de la Simulación
              </h2>
              <button
                onClick={downloadPDF}
                className="px-4 py-2 bg-white/20 text-white rounded-full hover:bg-white/30 transition-all duration-300 text-sm flex items-center gap-2 backdrop-blur-sm"
              >
                <IconDownload size={16} />
                Descargar PDF
              </button>
            </div>
            <iframe
              src={modalUrl}
              title="View Content"
              className="flex-1 w-full border-none rounded-b-2xl bg-gray-50"
            />
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mt-6">
        <div className="flex items-center gap-4">
          <div className="relative">
            <IconCalendar
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="pl-10 pr-4 py-2.5 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#1b2a49] focus:border-transparent text-sm shadow-sm"
              max={endDate || undefined}
            />
          </div>
          <div className="relative">
            <IconCalendar
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="pl-10 pr-4 py-2.5 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#1b2a49] focus:border-transparent text-sm shadow-sm"
              min={startDate || undefined}
            />
          </div>
          <button
            onClick={() => {
              if (!startDate && !endDate) {
                toast.error("Selecciona al menos una fecha para filtrar");
                return;
              }
              setActiveStartDate(startDate);
              setActiveEndDate(endDate);
              setCurrentPage(1);
              toast.success("Filtro aplicado", {
                description: "Los resultados han sido filtrados por fecha",
              });
            }}
            className="px-6 py-2.5 bg-[#4b4b4b] text-white rounded-full hover:bg-[#333] transition-all duration-300 text-[13px] shadow-md hover:shadow-xl flex items-center gap-2"
          >
            <i className="fas fa-filter"></i>
            Filtrar
          </button>
          {(activeStartDate || activeEndDate) && (
            <button
              onClick={() => {
                setStartDate("");
                setEndDate("");
                setActiveStartDate("");
                setActiveEndDate("");
                setCurrentPage(1);
                toast.success("Filtros eliminados", {
                  description: "Se han eliminado los filtros de fecha",
                });
              }}
              className="px-4 py-2.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all duration-300 text-sm shadow-md hover:shadow-xl flex items-center gap-2"
            >
              <i className="fas fa-times"></i>
              Limpiar
            </button>
          )}
        </div>

        <button
          onClick={onExportClick}
          className="group px-6 py-3 bg-gradient-to-r from-[#dc2626] to-[#991b1b] text-white rounded-full hover:from-[#991b1b] hover:to-[#dc2626] transition-all duration-300 text-sm shadow-md hover:shadow-xl flex items-center gap-2"
        >
          <IconDownload
            size={18}
            className="group-hover:scale-110 transition-transform"
          />
          Descargar
        </button>
      </div>
    </div>
  );
};

export default Simulator;